import { createApi } from '@reduxjs/toolkit/query/react';
import { Notistack } from '@/app/shared/components/Notistack';
import { getError } from '@/app/shared/utils/helpers';
import type { RootState } from '@/store';
import { baseQuery } from '@/store/api/baseQuery';
import { eventsApi, getDevice } from '@/store/api/eventsApi';
import { setUILoading } from '@/store/reducers/uiSlice';
import { toggleErrorModal } from '@/store/reducers/uploadSlice';
import type { AssetType, OcrDetail } from '../uploadStips/types';

export interface SendFilesResponse {
  ocrFailed: boolean;
  filesOcrDetails: OcrDetail[];
  failedFileNames: string[];
}

export interface SendFilesRequest {
  userContactId: string;
  docs: unknown[];
  guid: string;
}

export interface FetchUserDocumentsArgs {
  contactId?: string | null;
  guid?: string | null;
}

export interface CdxRedirectArgs {
  contactId: string;
  authId: string;
}

export interface StipRequestItems {
  entityType: number;
  entityTypeName: string;
  entityDisplayName: string;
  entityId: string;
  isEntityCoContact: boolean;
  stipReceivedDate: string | null;
  stipRequestId: string;
  stipRequestItemId: string;
  stipTypeId: string;
  stipTypeName: string;
  isStipDenied: boolean;
  deniedComments: string | null;
  relatedAssetId: string;
  stipTypeDescription: string;
  confidenceScore: number | null;
  validationMessages: string | null;
}

export interface UserDocuments {
  contactId: string;
  stipsRequestItems: StipRequestItems[];
}

export interface StipType {
  stipTypeId: string;
  stipTypeName: string;
  entityTypeId: number;
}

export interface Entities {
  assets: AssetType[];
  contacts: AssetType[];
  deals: AssetType[];
}

export const documentsApi = createApi({
  reducerPath: 'documentsApi',
  baseQuery,
  tagTypes: ['Documents', 'Assets', 'References'],
  endpoints: (builder) => ({
    fetchUserDocuments: builder.query<UserDocuments, FetchUserDocumentsArgs>({
      query: ({ contactId }) => `/stips/contacts/${contactId}`,

      providesTags: ['Documents'],

      async onQueryStarted({ contactId }, { dispatch, getState, queryFulfilled }) {
        const state = getState() as RootState;

        const contactIdValue = contactId || state.auth.contactId;

        if (!contactIdValue) {
          dispatch(toggleErrorModal(true));
          return;
        }

        try {
          await queryFulfilled;

          dispatch(
            eventsApi.endpoints.addEventLog.initiate({
              event: 'LoggedInSuccessfully',
            })
          );
        } catch (err: any) {
          Notistack.generalError(getError(err));
        }
      },
    }),

    fetchStipsTypes: builder.query<StipType[], void>({
      query: () => '/StipTypes',
    }),

    fetchAssets: builder.query<Entities, string>({
      query: (contactId) => `/contacts/${contactId}/entities`,
      providesTags: ['Assets'],
    }),

    sendFiles: builder.mutation<SendFilesResponse, SendFilesRequest>({
      async queryFn(arg, api, _extraOptions, baseQueryFn) {
        const dispatch = api.dispatch;
        dispatch(setUILoading(true));
        let network: string = 'Unknown';

        if ('connection' in navigator) {
          const nav = navigator as Navigator & {
            connection?: { type?: string };
          };
          network = nav.connection?.type ?? 'Wifi';
        }

        const body = {
          userContactId: arg.userContactId,
          stipDocuments: arg.docs,
          stipGuid: arg.guid,
          stipsDevice: getDevice(),
          networkType: network,
        };
        const result = await baseQueryFn({
          url: `/stips/${arg.guid}/contacts/${arg.userContactId}/docs`,
          method: 'POST',
          body,
        });

        if (result.error) {
          dispatch(setUILoading(false));
          return { error: result.error };
        }

        const data = result.data as {
          filesOcrDetails?: OcrDetail[];
          failedFileNames?: string[];
        };

        const ocrDetails = data?.filesOcrDetails ?? [];

        const failedFileNamesResponse = data?.failedFileNames ?? [];

        const ocrFailed = Array.isArray(ocrDetails) && ocrDetails.some((item) => item.isValid === false);
        dispatch(setUILoading(false));
        return {
          data: {
            ocrFailed,
            filesOcrDetails: ocrDetails,
            failedFileNames: failedFileNamesResponse,
          },
        };
      },
      invalidatesTags: ['Documents'],
    }),

    fetchTruStageUrl: builder.query<string, string>({
      query: (contactId) => `stips/contacts/${contactId}/truStage`,
      transformResponse: (response: string) => {
        const url = response;
        if (!url || typeof url !== 'string') {
          throw new Error('Invalid TruStage URL');
        }
        return url;
      },
    }),
    cdxRedirect: builder.mutation<unknown, CdxRedirectArgs>({
      query: ({ contactId, authId }) => ({
        url: '/stips/cdx-redirect-request',
        method: 'POST',
        body: {
          contactId,
          stipAuthId: authId,
        },
      }),
    }),
  }),
});

export const {
  useFetchUserDocumentsQuery,
  useFetchStipsTypesQuery,
  useFetchAssetsQuery,
  useSendFilesMutation,
  useLazyFetchTruStageUrlQuery,
  useCdxRedirectMutation,
} = documentsApi;
